import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'player',
    organization: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const roleOptions = [
    { value: 'scout', label: 'Scout' },
    { value: 'club', label: 'Club Partner' },
    { value: 'player', label: 'Player' },
    { value: 'coach', label: 'Coach/Technical Staff' },
    { value: 'keeper', label: 'Goalkeeper' }
  ];

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5242880) {
        setError('Image size must be less than 5MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/webp']?.includes(file?.type)) {
        setError('Only JPEG, PNG, and WebP images are allowed');
        return;
      }
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const uploadProfileImage = async (userId) => {
    if (!profileImage) return null;

    try {
      const fileExt = profileImage?.name?.split('.')?.pop();
      const fileName = `${userId}/profile-${Date.now()}.${fileExt}`;
      
      const { data, error: uploadError } = await supabase?.storage?.from('player-media')?.upload(fileName, profileImage, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      return fileName;
    } catch (err) {
      console.error('Error uploading image:', err);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { data, error: signUpError } = await signUp(
        formData?.email,
        formData?.password,
        {
          full_name: formData?.fullName,
          role: formData?.role,
          organization: formData?.organization
        }
      );

      if (signUpError) {
        setError(signUpError?.message || 'Failed to create account');
        setLoading(false);
        return;
      }

      if (data?.user) {
        let profilePhotoPath = null;
        if (profileImage && (formData?.role === 'player' || formData?.role === 'keeper')) {
          profilePhotoPath = await uploadProfileImage(data?.user?.id);
        }

        if (profilePhotoPath) {
          await supabase?.from('user_profiles')?.update({ profile_photo: profilePhotoPath })?.eq('id', data?.user?.id);
        }

        setSuccess(true);
        setTimeout(() => {
          navigate('/sign-in');
        }, 2000);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
            <div className="max-w-md mx-auto">
              <div className="bg-card rounded-lg border border-border p-6 md:p-8 shadow-lg text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={32} color="var(--color-success)" className="md:w-10 md:h-10" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Account Created Successfully!</h2>
                <p className="text-muted-foreground mb-4">
                  Your account is ready! You can now sign in and start using ProX11 Market.
                </p>
                <p className="text-sm text-muted-foreground">
                  Redirecting to sign in page...
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
          <div className="max-w-md mx-auto">
            <div className="bg-card rounded-lg border border-border p-6 md:p-8 shadow-lg">
              <div className="text-center mb-6 md:mb-8">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="UserPlus" size={32} color="var(--color-primary)" className="md:w-10 md:h-10" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2 font-headline">Create Account</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Join ProX11 Market today
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="text"
                  label="Full Name"
                  placeholder="Enter your full name"
                  value={formData?.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e?.target?.value })}
                  required
                />

                <Input
                  type="email"
                  label="Email Address"
                  placeholder="Enter your email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  required
                />

                <Select
                  label="Account Type"
                  options={roleOptions}
                  value={formData?.role}
                  onChange={(value) => setFormData({ ...formData, role: value })}
                  required
                />

                {(formData?.role === 'player' || formData?.role === 'keeper') && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Profile Picture (Optional)
                    </label>
                    <div className="flex items-center gap-4">
                      {imagePreview && (
                        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-primary">
                          <img src={imagePreview} alt="Profile preview" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleImageChange}
                          className="hidden"
                          id="profile-image-upload"
                        />
                        <label
                          htmlFor="profile-image-upload"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg cursor-pointer transition-colors duration-300"
                        >
                          <Icon name="Upload" size={16} />
                          <span className="text-sm">{profileImage ? 'Change Image' : 'Upload Image'}</span>
                        </label>
                        <p className="text-xs text-muted-foreground mt-1">Max 5MB, JPEG/PNG/WebP</p>
                      </div>
                    </div>
                  </div>
                )}

                <Input
                  type="text"
                  label="Organization (Optional)"
                  placeholder="Your club, agency, or organization"
                  value={formData?.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e?.target?.value })}
                />

                <Input
                  type="password"
                  label="Password"
                  placeholder="Create a password (min 6 characters)"
                  value={formData?.password}
                  onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                  required
                />

                <Input
                  type="password"
                  label="Confirm Password"
                  placeholder="Re-enter your password"
                  value={formData?.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e?.target?.value })}
                  required
                />

                {error && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
                    <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-error">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/sign-in" className="text-primary hover:underline font-medium">
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;